import { z } from "zod";

/**
 * Internal medicine chronic-care review — anthropometrics + BP + renal +
 * metabolic labs + managed-conditions checklist. BMI and eGFR (CKD-EPI 2021)
 * are derived deterministically; everything else is entered as measured.
 */

const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");

export const imConditionsSchema = z.object({
	diabetes: z.boolean().optional(),
	hypertension: z.boolean().optional(),
	dyslipidemia: z.boolean().optional(),
	hypothyroidism: z.boolean().optional(),
	ckd: z.boolean().optional(),
	ihd: z.boolean().optional(),
	copd: z.boolean().optional(),
	asthma: z.boolean().optional(),
});
export type ImConditions = z.infer<typeof imConditionsSchema>;

export const internalMedAssessmentApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	review_date: dateStr,
	weight_kg: z.number().nullable(),
	height_cm: z.number().nullable(),
	bmi: z.number().nullable(),
	bp_systolic: z.number().int().nullable(),
	bp_diastolic: z.number().int().nullable(),
	creatinine: z.number().nullable(),
	egfr: z.number().nullable(),
	ckd_stage: z.string().nullable(),
	hba1c: z.number().nullable(),
	fasting_glucose: z.number().int().nullable(),
	total_cholesterol: z.number().int().nullable(),
	ldl: z.number().int().nullable(),
	hdl: z.number().int().nullable(),
	triglycerides: z.number().int().nullable(),
	conditions: imConditionsSchema.nullable(),
	assessment: z.string().nullable(),
	plan: z.string().nullable(),
	notes: z.string().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
});
export type InternalMedAssessmentApi = z.infer<
	typeof internalMedAssessmentApiSchema
>;

const intIn = (min: number, max: number) =>
	z.coerce.number().int().min(min).max(max).nullable().optional();

export const createInternalMedAssessmentInputSchema = z.object({
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().optional(),
	review_date: dateStr,
	weight_kg: z.coerce.number().min(0.5).max(500).nullable().optional(),
	height_cm: z.coerce.number().min(20).max(260).nullable().optional(),
	bp_systolic: intIn(40, 300),
	bp_diastolic: intIn(20, 200),
	creatinine: z.coerce.number().min(0.1).max(25).nullable().optional(),
	hba1c: z.coerce.number().min(2).max(20).nullable().optional(),
	fasting_glucose: intIn(20, 1000),
	total_cholesterol: intIn(30, 800),
	ldl: intIn(10, 600),
	hdl: intIn(5, 200),
	triglycerides: intIn(20, 2000),
	conditions: imConditionsSchema.nullable().optional(),
	assessment: z.string().max(2000).nullable().optional(),
	plan: z.string().max(2000).nullable().optional(),
	notes: z.string().max(2000).nullable().optional(),
});
export type CreateInternalMedAssessmentInput = z.infer<
	typeof createInternalMedAssessmentInputSchema
>;

// BMI + its category live in clinical.ts (computeBmi / bmiCategory) — reused here.

// ===== Deterministic eGFR (CKD-EPI 2021, race-free) =====

/**
 * CKD-EPI 2021 creatinine equation (race-free). Pure + deterministic.
 *   eGFR = 142 × min(Scr/κ,1)^α × max(Scr/κ,1)^-1.200 × 0.9938^Age × (1.012 if female)
 * κ = 0.7 (female) / 0.9 (male); α = -0.241 (female) / -0.302 (male); Scr mg/dL.
 * Returns mL/min/1.73m² rounded to 1 dp, or null when inputs are missing/invalid.
 */
export function computeEgfrCkdEpi2021(
	creatinineMgDl: number | null | undefined,
	ageYears: number | null | undefined,
	sex: "male" | "female" | string | null | undefined,
): number | null {
	if (creatinineMgDl == null || ageYears == null) return null;
	if (!Number.isFinite(creatinineMgDl) || !Number.isFinite(ageYears))
		return null;
	if (creatinineMgDl <= 0 || ageYears < 18 || ageYears > 120) return null; // adult equation only
	const female = sex === "female";
	const kappa = female ? 0.7 : 0.9;
	const alpha = female ? -0.241 : -0.302;
	const scrK = creatinineMgDl / kappa;
	const egfr =
		142 *
		Math.min(scrK, 1) ** alpha *
		Math.max(scrK, 1) ** -1.2 *
		0.9938 ** ageYears *
		(female ? 1.012 : 1);
	return Math.round(egfr * 10) / 10;
}

export type CkdStage = "G1" | "G2" | "G3a" | "G3b" | "G4" | "G5";

/** KDIGO CKD GFR category from eGFR (mL/min/1.73m²). */
export function ckdStage(egfr: number | null | undefined): CkdStage | null {
	if (egfr == null || !Number.isFinite(egfr)) return null;
	if (egfr >= 90) return "G1";
	if (egfr >= 60) return "G2";
	if (egfr >= 45) return "G3a";
	if (egfr >= 30) return "G3b";
	if (egfr >= 15) return "G4";
	return "G5";
}
